<template>
  <div :class="`drag-selector-wrapper ${disabled ? 'disabled' : ''}`" @mousedown="handleMouseDown">
    <div class="drag-area-box" :style="selectAreaStyle"></div>
    <slot></slot>
  </div>
</template>

<script>
import emitter from '@/mixins/emitter'
import { debounce } from '@/helpers/debounce'
import { throttle } from '@/helpers/throttle'
import { isControl } from '@/helpers/isControl'

/**
 * DRAG_ENABLED_TIMEOUT
 * Desc: Time when enable drag area if there is mouse down and mouse barely moved
 * Info about duration mouse click: average mouse click lasts 85ms, with Q3 = 95ms, Q1= 75ms; with an upper fence of 135ms and a lower fence of 55ms.
 * Resource: https://ieeexplore.ieee.org/document/9851764
 */
const DRAG_ENABLED_TIMEOUT = 135
/**
 * DRAG_ENABLED_DISTANCE
 * Desc: Movement distance betwen mouse down and mouseMove, which will depend when enable drag area
 */
const DRAG_ENABLED_DISTANCE = 15

const POINT_MODEL = ['offsetX', 'offsetY', 'clientX', 'clientY']

export default {
  name: 'DragSelector',
  mixins: [emitter],
  props: {
    dragSelectorElements: {
      type: Array,
      default() {
        return []
      },
    },
    identifyElementBy: {
      type: String,
      default: 'id'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    cancelOverView: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      dragging: false,
      handleMouseMoveThrottled: () => {},
      emitChangeThrottled: () => {},
      startPoint: {
        offsetX: 0,
        offsetY: 0,
      },
      endPoint: {
        offsetX: 0,
        offsetY: 0,
      },
      point: {
        offsetX: 0,
        offsetY: 0,
      },
      childItems: [],
      childrenDOMPoints: [],
      childrenDOMPointsNewSelection: [],
      browserPoint: {},
      selfPoint: {
        clientX: 0,
        clientY: 0,
      },
    }
  },
  created() {
    if (!this.disabled) {
      this.registerEvents()
    }
  },
  mounted() {
    if (!this.disabled) {
      this.handleMouseMoveThrottled = throttle(this.handleMouseMove)
      this.emitChangeThrottled = throttle(this.emitChange, 200)
    }
  },
  methods: {
    registerEvents() {
      this.$on('on-drag-selector-item-add', item => {
        if (item) this.childItems.push(item)
      })
      this.$on('on-drag-selector-item-remove', item => {
        const index = this.childItems.indexOf(item)
        if (index >= 0) this.childItems.splice(index, 1)
      })
      this.$on('drag-selector-item-change', ({ selected, value }) => {
        const index = this.dragSelectorElements.findIndex(el => el[this.identifyElementBy] === value[this.identifyElementBy])

        if (selected) {
          this.dragSelectorElements.push(value)
        } else if (index !== -1) {
          this.dragSelectorElements.splice(index, 1)
        }
        this.emitChangeThrottled()
      })
    },
    emitChange() {
      this.$emit('change', this.dragSelectorElements)
      this.$emit('input', this.dragSelectorElements)
    },
    handleMouseDown(e) {
      if (this.disabled) return
      this.childrenDOMPointsNewSelection = []
      this.enableDraggingDebounced()
      if (!isControl(e)) {
        this.cancelAllSelect()
      }
      this.$nextTick(() => {
        this.resetPoint(e)
        this.updatePointData(this.point, e)
        window.addEventListener('mouseup', this.handleMouseUp)
        window.addEventListener('mousemove', this.handleMouseMoveThrottled)
      })
    },
    enableDraggingDebounced() {
      debounce(
        () => this.dragging = true,
        DRAG_ENABLED_TIMEOUT,
        'DragSelectorEnable',
      )
    },
    clearDraggingDebounced() {
      debounce(
        () => {},
        1,
        'DragSelectorEnable',
      )
    },
    enableDraggingByDistance(e) {
      const startClientX = this.startPoint.clientX
      const startClientY = this.startPoint.clientY
      const endClientX = e.clientX
      const endClientY = e.clientY

      const a = startClientX - endClientX
      const b = startClientY - endClientY
      const distance = Math.hypot(a,b)

      const enableDragging = distance >= DRAG_ENABLED_DISTANCE

      if (enableDragging) {
        this.clearDraggingDebounced()
        this.dragging = true
        return true
      }

      return false
    },
    handleMouseMove(e) {
      if (this.disabled) return
      if (!this.dragging) {
        const isDragging = this.enableDraggingByDistance(e)

        if (!isDragging) {
          return;
        }
      }
      this.updatePointData(this.endPoint, e)
      this.updatePointData(this.point, e)
      this.$nextTick(() => {
        const len = this.childrenDOMPoints.length
        for (let i = 0; i < len; i++) {
          const child = this.childrenDOMPoints[i]
          const dragSelectorElement = child.instance.dragSelectorElement
          const isSelectedWithThisDrag = this.childrenDOMPointsNewSelection.find(el => el?.[this.identifyElementBy] === dragSelectorElement?.[this.identifyElementBy] && dragSelectorElement?.[this.identifyElementBy] !== undefined)

          if (!isSelectedWithThisDrag && isControl(e) && child.instance.selected) {
            return
          }

          const isSelected = this.checkIfChildInSelectArea(child.point)
          this.childrenDOMPointsNewSelection = [
            ...this.childrenDOMPointsNewSelection,
            dragSelectorElement
          ]
          child.instance.handleSelectChange(isSelected)
        }
      })
    },
    cancelAllSelect() {
      this.childItems.forEach(child => {
        child.handleSelectChange(false)
      })

      if (this.cancelOverView) {
        this.dragSelectorElements.splice(0, this.dragSelectorElements.length)
        this.emitChangeThrottled()
      }
    },
    /**
     * Determine whether the frame selection and sub-elements intersect
     *    The left margin of the box selection is less than the left margin of the child element plus its width
     *    The left margin of the box selection plus its width is greater than the left margin of the child element
     *    The top margin of the box selection is less than the top margin of the child element plus its height
     *    The top margin of the box selection plus its height is greater than the top margin of the child element
     */
    checkIfChildInSelectArea(childItem) {
      const startClientX = this.startPoint.clientX
      const startClientY = this.startPoint.clientY
      const endClientX = this.endPoint.clientX
      const endClientY = this.endPoint.clientY
      const selectPoint = {
        left: Math.min(startClientX, endClientX),
        top: Math.min(startClientY, endClientY),
        width: Math.abs(startClientX - endClientX),
        height: Math.abs(startClientY - endClientY),
      }
      return (
        selectPoint.left <= childItem.left + childItem.width &&
        selectPoint.left + selectPoint.width >= childItem.left &&
        selectPoint.top <= childItem.top + childItem.height &&
        selectPoint.top + selectPoint.height >= childItem.top
      )
    },
    handleMouseUp(e) {
      this.clearDraggingDebounced()
      this.dragging = false
      this.updatePointData(this.point, e)
      this.resetPoint(e)
      window.removeEventListener('mouseup', this.handleMouseUp)
      window.removeEventListener('mousemove', this.handleMouseMoveThrottled)
    },
    /**
     * Update coordinate values
     * @param pointObj Update the coordinate value in pointObj through the keys specified by pointData and POINT MODEL
     * @param pointData Event object when mouse moves
     */
    updatePointData(pointObj, pointData) {
      POINT_MODEL.forEach(key => {
        this.$set(pointObj, key, pointData[key])
      })
    },
    initBrowserPoint() {
      this.browserPoint = {
        clientWidth: document.body.clientWidth,
        clientHeight: document.body.clientHeight,
        scrollWidth: document.body.scrollWidth,
        scrollHeight: document.body.scrollHeight,
      }
    },
    collectDOMPoints() {
      const DOMRect = this.$el.getBoundingClientRect()
      this.selfPoint = {
        clientX: DOMRect.left || DOMRect.x,
        clientY: DOMRect.top || DOMRect.y,
      }
      this.childrenDOMPoints = this.childItems.map(child => ({
        point: child.$el.getBoundingClientRect(),
        instance: child,
      }))
    },
    resetPoint(e) {
      this.updatePointData(this.startPoint, e)
      this.updatePointData(this.endPoint, e)
    },
  },
  computed: {
    hasScrollX() {
      return !(this.browserPoint.clientWidth - this.browserPoint.scrollWidth)
    },
    hasScrollY() {
      return !(this.browserPoint.clientHeight - this.browserPoint.scrollHeight)
    },
    selectAreaStyle() {
      const startClientX = this.startPoint.clientX - this.selfPoint.clientX
      const endClientX = this.endPoint.clientX - this.selfPoint.clientX
      const startClientY = this.startPoint.clientY - this.selfPoint.clientY
      const endClientY = this.endPoint.clientY - this.selfPoint.clientY
      const { left, top, width, height } = {
        left: Math.min(startClientX, endClientX),
        top: Math.min(startClientY, endClientY),
        width: Math.abs(startClientX - endClientX),
        height: Math.abs(startClientY - endClientY),
      }
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        zIndex: 444,
        backgroundColor: 'rgba(51, 51, 51, 0.5)',
        position: 'absolute',
      }
    },
  },
  watch: {
    // dragging: 'handleDraggingChange'
    dragging(val) {
      if (val) {
        this.$emit('drag-start')
        this.collectDOMPoints()
        this.initBrowserPoint()
      } else {
        this.$emit('drag-end')
      }
    },
  },
}
</script>
<style lang="scss">
.drag-selector-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  user-select: none;

  &:not(.disabled) {
    cursor: crosshair;
  }
}

.drag-area-box {
  pointer-events: none;
}
</style>
